import React, { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./SearchOptions.scss";
import SearchOption from "../../SearchAgent/SearchOption/searchoption";
import {
  HOTELS,
  PACKAGES,
  PLANES,
  ORGANIZED_TOURS,
  useSearch,
  CRUISE_PAYMENT,
  CAMPAIGNS,
} from "../../../../contexts/SearchContext";
import { useNavigate } from "react-router-dom";
import { RiHotelFill } from "react-icons/ri";
import { FaPlane } from "react-icons/fa";
import { MdOutlineDirectionsBus } from "react-icons/md";
import { IoBoat } from "react-icons/io5";
import { Megaphone } from "lucide-react";
import { ROUTES } from "../../../../Routes/Routes";
import { SHOW_FLIGHT_BOOKING_MOCKUP_IN_SEARCHOFFER_PLANES_TAB } from "../../../../config/flightBookingMockupSearchOffer";
import { useAgentEntitlements } from "../../../../features/agentEntitlements/useAgentEntitlements";

const SearchOptions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { searchTab, setSearchTab: ogSetSearchTab } = useSearch();
  const {
    canSearchHotels,
    canSearchPackages,
    canSearchFlights,
    canOrganizedTours,
    status: entitlementsStatus,
  } = useAgentEntitlements();
  const wrapperRef = useRef(null);

  const setSearchTab = (tab) => {
    if (tab === PLANES) {
      if (entitlementsStatus === "ok" && !canSearchFlights) return;
      ogSetSearchTab(PLANES, { source: "user_click" });
      if (SHOW_FLIGHT_BOOKING_MOCKUP_IN_SEARCHOFFER_PLANES_TAB) {
        navigate("/p/dashboard/searchoffer");
        return;
      }
      navigate(`/${ROUTES.FLIGHT_BOOKING}`);
      return;
    }

    if (tab === CRUISE_PAYMENT) {
      navigate("/p/dashboard/cruise-payment");
      ogSetSearchTab(CRUISE_PAYMENT, { source: "user_click" });
      return;
    }

    if (tab === CAMPAIGNS) {
      ogSetSearchTab(CAMPAIGNS, { source: "user_click" });
      navigate("/p/dashboard/searchoffer");
      return;
    }

    if (tab === ORGANIZED_TOURS) {
      if (entitlementsStatus === "ok" && !canOrganizedTours) return;
      ogSetSearchTab(ORGANIZED_TOURS, { source: "user_click" });
      navigate("/p/dashboard/searchoffer");
      return;
    }

    ogSetSearchTab(tab, { source: "user_click" });
    navigate("/p/dashboard/searchoffer");
  };

  // Scroll to center selected button on mobile
  useEffect(() => {
    if (wrapperRef.current && window.innerWidth <= 768) {
      const wrapper = wrapperRef.current;
      const selectedButton = wrapper.querySelector(`[data-tab-id="${searchTab}"]`);
      
      if (selectedButton) {
        const wrapperRect = wrapper.getBoundingClientRect();
        const buttonRect = selectedButton.getBoundingClientRect();
        const scrollLeft = selectedButton.offsetLeft - (wrapperRect.width / 2) + (buttonRect.width / 2);
        
        wrapper.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [searchTab]);

  const allCategories = useMemo(() => {
    // G.4E — Dynamic Packages removed from active search product picker.
    const gate = entitlementsStatus === "ok";
    const blockHotels = gate && !canSearchHotels;
    const blockPackages = gate && !canSearchPackages;
    const blockPlanes = gate && !canSearchFlights;
    const blockOrganized = gate && !canOrganizedTours;

    const categories = [
      {
        id: PACKAGES,
        title: t("searchagent.packages"),
        icon: (
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '2px', flexShrink: 0 }}>
            <RiHotelFill style={{ fontSize: '1em' }} />
            <FaPlane style={{ fontSize: '0.7em', marginLeft: '-2px', opacity: 0.95 }} />
          </div>
        ),
        disabled: blockPackages,
      },
      {
        id: HOTELS,
        title: t("searchagent.hotels"),
        icon: <RiHotelFill />,
        disabled: blockHotels,
      },
      {
        id: ORGANIZED_TOURS,
        title: t("searchagent.organizedtours"),
        icon: <MdOutlineDirectionsBus />,
        disabled: blockOrganized,
      },
      {
        id: PLANES,
        title: t("searchagent.planes"),
        icon: <FaPlane />,
        disabled:
          !SHOW_FLIGHT_BOOKING_MOCKUP_IN_SEARCHOFFER_PLANES_TAB || blockPlanes,
      },
      {
        id: CAMPAIGNS,
        title: t("searchagent.campaigns", { defaultValue: "קמפיינים" }),
        icon: <Megaphone size={18} strokeWidth={2.25} aria-hidden />,
      },
      {
        id: CRUISE_PAYMENT,
        title: t("searchagent.cruisepayment"),
        icon: <IoBoat />,
      },
    ];

    return categories;
  }, [
    t,
    entitlementsStatus,
    canSearchHotels,
    canSearchPackages,
    canSearchFlights,
    canOrganizedTours,
  ]);

  return (
    <div className="searchAgent-search-option-wrapper" ref={wrapperRef}>
      {/* Show all categories */}
      {allCategories.map((category) => (
        <SearchOption
          key={category.id}
          id={category.id}
          selected={searchTab === category.id}
          title={category.title}
          action={setSearchTab}
          icon={category.icon}
          disabled={category.disabled}
        />
      ))}
    </div>
  );
};

export default SearchOptions;
